/* eslint-disable max-lines */
import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Button, Divider, Form, Message, Segment } from "semantic-ui-react";

import { AccordionQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { getAnswerToQuestionWithId, getQuestionsFromQuestionnaire, UpdateAnswer } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { clearQuestionnaire, submitInvalidQuestionnaire, updateQuestion } from "@teamprevent/common/questionnaires/actions";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { CustomQuestionConfig, MasterQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import { MultiCheckbox, MultiCheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiCheckboxQuestion";
import { RadioButton, RadioButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { QuestionAnswer, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { AnsweredQuestionWithId, ConsultationDetails } from "@teamprevent/common/model";
import { getAdminProfessionDisplayName } from "@teamprevent/common/adminUser/models";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";

import {
    coMorbidConditionsOptions,
    ConfirmedAnswer,
    ConsentAndReport,
    consentAndReportOptions,
    ConsentedAnswer,
    ConsentToReleaseTheReportAnswers,
    ConsultationSectionIds,
    currentWorkStatusOptions,
    getQuestionConfig,
    MHP_TREATMENT_CONSULTATION,
    outcomeFinalSessionOnlyOptions,
    provisionalDiagnosisOptions,
    RiskAssessmentCompleted,
    riskAssessmentCompletedOptions,
    RiskLetterToGP,
    riskLetterToGPOptions,
    sessionsSignpostingDischargeOptions,
    signpostToOtherServicesOptions,
    treatmentCompletedInTodaysSessionOptions
} from "../models";
import { useAdminUserOptions } from "../../crud/adminUser/useAdminUserOptions";
import * as questionIds from "../mhpTreatmentConsultationQuestionIds";
import { ApplicationState } from "../../applicationState";
import { getUserDisplayName } from "../../crud/adminUser/model";
import { OhCaseDetailModel } from "../../crud/ohCase";
import { EmployeeRecordDetailModel } from "../../crud/employeeRecord";
import {
    ConsultationMethodOption,
    consultationMethodOptions,
    PsychologicalAssessmentOutcome,
    psychologicalAssessmentOutcomeQuestionOptions
} from "../../clinicalNotes/models";
import { AddTasksInputs } from "../../tasks/components/addTasksInputs";
import { mapAddTaskFormModelToCreateTaskDto, TaskType } from "../../task/model";
import { loadMHPTreatmentConsultation, saveMHPTreatmentConsultation, submitMHPTreatmentConsultation } from "../actions";
import { mhpTreatmentApi } from "../mhpTreatmentApi";
import { TasksApi } from "../../tasks/tasksApi";
import { AddNextTaskFormModel } from "../../tasks/models";

import { IAPTQuestionnaire } from "./IAPTQuestionnaire";

interface Props {
    customerId: string;
    employeeRecord: EmployeeRecordDetailModel;
    ohCase: OhCaseDetailModel;
    task: TaskModel;
    readonly: boolean;
}

export const MHPTreatmentConsultationSections: React.FunctionComponent<Props> = (props: Props) => {

    const dispatch = useDispatch();

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [nextTasks, setNextTasks] = React.useState<AddNextTaskFormModel[]>([]);
    const [nextTaskTypes, setNextTaskTypes] = React.useState<TaskType[] | null>(null);
    const [consultationDetails, setConsultationDetails] = React.useState<ConsultationDetails | null>(null);

    const currentUser = useSelector((state: ApplicationState) => state.currentUser);
    const adminUsers = useSelector((state: ApplicationState) => state.adminUsers);
    const questionnaire = useSelector((state: ApplicationState) => state.questionnaires[MHP_TREATMENT_CONSULTATION]);
    const questions = getQuestionsFromQuestionnaire(questionnaire);

    React.useEffect(() => {
        (async () => {
            if (props.ohCase?.id && props.task?.id && !props.readonly) {
                const [taskTypes, details] = await Promise.all([
                    mhpTreatmentApi.getConsultationNextTaskTypes(),
                    mhpTreatmentApi.getMHPTreatmentConsultationDetails(props.ohCase?.id, props.task?.id)
                ]);

                setNextTaskTypes(taskTypes);
                setConsultationDetails(details);
            }
        })();

        return () => {
            dispatch(clearQuestionnaire(MHP_TREATMENT_CONSULTATION));
        };
    }, [props.ohCase?.id, props.task?.id, props.readonly, dispatch]);

    const loadQuestionnaire = React.useCallback(async () => {
        await dispatch(loadMHPTreatmentConsultation(props.ohCase?.id, props.task?.id));
    }, [dispatch, props.ohCase?.id, props.task?.id]);

    const consentAndReportConsentAnswer = getAnswerToQuestionWithId(questions, questionIds.ConsentAndReportConsent) as ConsentAndReport;

    const updateAnswer = React.useCallback((updatedQuestion: UpdateAnswer) => {
        dispatch(updateQuestion({ ...updatedQuestion, questionnaireKey: MHP_TREATMENT_CONSULTATION  }));
    }, [dispatch]);

    const completedByAdminUserId = currentUser.id;
    const completedByAdminUser = React.useMemo(() => adminUsers.find(u => u.id === completedByAdminUserId), [completedByAdminUserId, adminUsers]);
    const completedByFullName = React.useMemo(() => completedByAdminUser ? getUserDisplayName(completedByAdminUser, true, true) : "", [completedByAdminUser]);
    const completedByFullTitle = React.useMemo(() => getAdminProfessionDisplayName(completedByAdminUser?.profession), [completedByAdminUser?.profession]);
    const completedByQualification = React.useMemo(() => completedByAdminUser?.qualification ?? "", [completedByAdminUser?.qualification]);

    const setCompetedBy = React.useCallback(() => {
        if (!props.readonly) {
            updateAnswer({
                sectionId: ConsultationSectionIds.CompletedOnBehalfOf,
                id: questionIds.CompletedBy,
                question: { question: "", answer: completedByAdminUserId, isValid: true }
            });
            updateAnswer({
                sectionId: ConsultationSectionIds.CompletedOnBehalfOf,
                id: questionIds.CompletedByName,
                question: { question: "", answer: completedByFullName, isValid: true }
            });
            updateAnswer({
                sectionId: ConsultationSectionIds.CompletedOnBehalfOf ,
                id: questionIds.CompletedByTitle,
                question: { question: "", answer: completedByFullTitle, isValid: true }
            });
            updateAnswer({
                sectionId: ConsultationSectionIds.CompletedOnBehalfOf,
                id: questionIds.CompletedByQualification,
                question: { question: "", answer: completedByQualification, isValid: true }
            });
        }
    }, [completedByAdminUserId, completedByFullName, completedByFullTitle, completedByQualification, props.readonly, updateAnswer]);

    React.useEffect(() => setCompetedBy(), [setCompetedBy]);

    const saveQuestionnaire = React.useCallback(async (questionsToSave: AnsweredQuestionWithId[], eTag: string) => {
        setCompetedBy();
        await dispatch(saveMHPTreatmentConsultation(props.ohCase.id, props.task.id, questionsToSave, eTag));
    }, [dispatch, props.ohCase.id, props.task.id, setCompetedBy]);

    const submitQuestionnaire = React.useCallback(async (): Promise<boolean> => {
        setSubmitted(true);

        if (questions.some(q => !q.isValid)) {
            await dispatch(submitInvalidQuestionnaire(MHP_TREATMENT_CONSULTATION));
            return false;
        }

        const noNextTasks = !nextTasks || nextTasks?.length === 0;
        if (consentAndReportConsentAnswer === ConsentAndReport.No && (noNextTasks || nextTasks?.some(t => !t.isValid))) {
            await dispatch(submitInvalidQuestionnaire(MHP_TREATMENT_CONSULTATION));
            return false;
        }

        const createTasks = nextTasks?.map(t => mapAddTaskFormModelToCreateTaskDto(t.task, props.ohCase?.id));
        await dispatch(submitMHPTreatmentConsultation(props.ohCase?.id, props.task?.id, createTasks));
        await dispatch(navigateToParentRoute());

        return true;
    }, [consentAndReportConsentAnswer, dispatch, nextTasks, props.ohCase?.id, props.task?.id, questions]);

    const updateSnippets = React.useCallback((snippetText: string, sectionId: string, id: string, questionText: string) => {
        const riskAssessmentRecommendationsAnswer = getAnswerToQuestionWithId(questions, id);
        const question = {
            question: questionText,
            answer: (riskAssessmentRecommendationsAnswer ? riskAssessmentRecommendationsAnswer + " " : "") + snippetText,
            isValid: true
        };
        updateAnswer({ sectionId, id, question });
    }, [questions, updateAnswer]);

    const onPsychologicalWellbeingScoreChange = React.useCallback((questionId: string, answer: QuestionAnswer) => {

        const questionConfig = getQuestionConfig(questionId) as MasterQuestionConfig;
        const questionText = questionConfig.questionText;

        let newAnswer: QuestionAnswer = null;
        if ((answer as string).length === 0) {
            newAnswer = PsychologicalAssessmentOutcome.NotScored;
        }
        else {
            const numberAnswer = +answer;
            switch (questionId) {
                case questionIds.PsychologicalWellbeingPHQ9Outcome:
                    if (numberAnswer < 5) {
                        newAnswer = PsychologicalAssessmentOutcome.NoDepression;
                    } else if (numberAnswer < 10) {
                        newAnswer = PsychologicalAssessmentOutcome.MildDepression;
                    } else if (numberAnswer < 15) {
                        newAnswer = PsychologicalAssessmentOutcome.ModerateDepression;
                    } else if (numberAnswer < 20) {
                        newAnswer = PsychologicalAssessmentOutcome.ModeratelySevereDepression;
                    } else {
                        newAnswer = PsychologicalAssessmentOutcome.SevereDepression;
                    }
                    break;
                case questionIds.PsychologicalWellbeingGAD7Outcome:
                    if (numberAnswer < 5) {
                        newAnswer = PsychologicalAssessmentOutcome.NoAnxiety;
                    } else if (numberAnswer < 10) {
                        newAnswer = PsychologicalAssessmentOutcome.MildAnxiety;
                    } else if (numberAnswer < 15) {
                        newAnswer = PsychologicalAssessmentOutcome.ModerateAnxiety;
                    } else {
                        newAnswer = PsychologicalAssessmentOutcome.SevereAnxiety;
                    }
                    break;
                case questionIds.PsychologicalWellbeingWSASOutcome:
                    if (numberAnswer <= 10) {
                        newAnswer = PsychologicalAssessmentOutcome.NoFunctionalImpairment;
                    } else if (numberAnswer <= 20) {
                        newAnswer = PsychologicalAssessmentOutcome.MildToModerateFunctionalImpairment;
                    } else {
                        newAnswer = PsychologicalAssessmentOutcome.ModerateOrSevereFunctionalImpairment;
                    }
                    break;
                default:
                    break;
            }
        }

        updateAnswer({
            sectionId: ConsultationSectionIds.QuestionnairesAndRisk,
            id: questionId,
            question: {
                question: questionText,
                answer: newAnswer,
                isValid: true
            }
        });
    }, [updateAnswer]);

    const completedOnBehalfOfQuestionAnswer = getAnswerToQuestionWithId(questions, questionIds.CompletedOnBehalfOf);
    React.useEffect(() => {
        if (!props.readonly) {
            const completedOnBehalfOfAdminUserId = completedOnBehalfOfQuestionAnswer as string;
            const completedOnBehalfOfAdminUser = adminUsers.find(u => u.id === completedOnBehalfOfAdminUserId);
            const completedOnBehalfOfFullName = completedOnBehalfOfAdminUser ? getUserDisplayName(completedOnBehalfOfAdminUser, true, true) : "";
            const completedOnBehalfOfFullTitle = getAdminProfessionDisplayName(completedOnBehalfOfAdminUser?.profession);
            const completedOnBehalfOfQualification = completedOnBehalfOfAdminUser?.qualification ?? "";
            updateAnswer({
                sectionId: ConsultationSectionIds.CompletedOnBehalfOf,
                id: questionIds.CompletedOnBehalfOfName,
                question: { question: "", answer: completedOnBehalfOfFullName, isValid: true }
            });
            updateAnswer({
                sectionId: ConsultationSectionIds.CompletedOnBehalfOf ,
                id: questionIds.CompletedOnBehalfOfTitle,
                question: { question: "", answer: completedOnBehalfOfFullTitle, isValid: true }
            });
            updateAnswer({
                sectionId: ConsultationSectionIds.CompletedOnBehalfOf,
                id: questionIds.CompletedOnBehalfOfQualification,
                question: { question: "", answer: completedOnBehalfOfQualification, isValid: true }
            });
        }
    }, [props.readonly, completedOnBehalfOfQuestionAnswer, adminUsers, updateAnswer]);

    const employeeAddress = React.useMemo(() => {
        return props.employeeRecord ? [props.employeeRecord.addressLine1, props.employeeRecord.postCode].filter(x => x).join(" ") : null;
    }, [props.employeeRecord]);

    const introductionAndDataPrivacyConfirmOtherIdentifierDetailsQuestionConfig =
        getQuestionConfig(questionIds.IntroductionAndDataPrivacyConfirmOtherIdentifierDetails) as MasterQuestionConfig;

    const consultationMethodAnswer = getAnswerToQuestionWithId(questions, questionIds.IntroductionAndDataPrivacyConsultationMethod);

    const riskAssessmentCompletedAnswer = getAnswerToQuestionWithId(questions, questionIds.RiskAssessmentCompleted) as RiskAssessmentCompleted;

    const riskLetterToGPAnswer = getAnswerToQuestionWithId(questions, questionIds.ClinicalNotesRiskLetterToGeneralPractitioner) as RiskLetterToGP;

    const clinicalDetailsCoMorbidConditionsAnswer = getAnswerToQuestionWithId(questions, questionIds.ClinicalDetailsCoMorbidConditions);
    const showClinicalDetailsCoMorbidConditionsOtherQuestion =
        (clinicalDetailsCoMorbidConditionsAnswer as string[])?.includes(coMorbidConditionsOptions.Other);

    const clinicalDetailsTreatmentCompletedInTodaysSessionAnswer =
        getAnswerToQuestionWithId(questions, questionIds.ClinicalDetailsTreatmentCompletedInTodaysSession);
    const showClinicalDetailsTreatmentCompletedInTodaysSessionOtherQuestion =
        (clinicalDetailsTreatmentCompletedInTodaysSessionAnswer as string[])?.includes(treatmentCompletedInTodaysSessionOptions.Other);

    const clinicalDetailsSignpostToOtherServicesAnswer =
        getAnswerToQuestionWithId(questions, questionIds.ClinicalDetailsSignpostToOtherServices);
    const showClinicalDetailsSignpostToOtherServicesOtherQuestion =
        (clinicalDetailsSignpostToOtherServicesAnswer as string[])?.includes(signpostToOtherServicesOptions.Other);

    const scopeOfReportAnswer = getAnswerToQuestionWithId(questions, questionIds.ConsentAndReportScopeOfReport) as ConsentToReleaseTheReportAnswers;

    const removeAppointment = React.useCallback(
        () => new TasksApi().removeAppointment(props.task?.caseId, props.task?.id),
        [props.task?.caseId, props.task?.id]
    );

    const onIntroductionAndDataPrivacyConfirmOtherIdentifierDetailsSelect = React.useCallback((selectedSnippet) => updateSnippets(
        selectedSnippet,
        ConsultationSectionIds.IntroductionAndDataPrivacy,
        introductionAndDataPrivacyConfirmOtherIdentifierDetailsQuestionConfig.id,
        introductionAndDataPrivacyConfirmOtherIdentifierDetailsQuestionConfig.questionText
    ), [
        introductionAndDataPrivacyConfirmOtherIdentifierDetailsQuestionConfig.id,
        introductionAndDataPrivacyConfirmOtherIdentifierDetailsQuestionConfig.questionText,
        updateSnippets]);

    const onPsychologicalWellbeingPHQ9ScoreAnswerChange = React.useCallback((answer) => onPsychologicalWellbeingScoreChange(
        questionIds.PsychologicalWellbeingPHQ9Outcome,
        answer
    ), [onPsychologicalWellbeingScoreChange]);

    const onPsychologicalWellbeingGAD7ScoreAnswerChange = React.useCallback((answer) => onPsychologicalWellbeingScoreChange(
        questionIds.PsychologicalWellbeingGAD7Outcome,
        answer
    ), [onPsychologicalWellbeingScoreChange]);

    const onPsychologicalWellbeingWSASScoreAnswerChange = React.useCallback((answer) => onPsychologicalWellbeingScoreChange(
        questionIds.PsychologicalWellbeingWSASOutcome,
        answer
    ), [onPsychologicalWellbeingScoreChange]);

    return (
        <AccordionQuestionnaireContainer
            autoSaveQuestionnaire={saveQuestionnaire}
            loadQuestionnaire={loadQuestionnaire}
            submitQuestionnaire={submitQuestionnaire}
            readonly={props.readonly}
            questionnaireKey={MHP_TREATMENT_CONSULTATION}
            otherActionButtons={props.readonly ? null : (
                <a
                    href={`/customers/${props.customerId}/employeeRecords/${props.employeeRecord?.id}`
                        + `/ohCases/${props.ohCase?.id}/${props.task?.id}/previewMHPTreatmentReport`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Button type="button" content="Preview Report" />
                </a>
            )}
            questionnaireType={QuestionnaireTypes.ClinicalNotes}
            task={props.task}
            removeAppointment={removeAppointment}
        >
            <AccordionQuestionnaireSection
                title="Completed on Behalf of"
                key={ConsultationSectionIds.CompletedOnBehalfOf}
                id={ConsultationSectionIds.CompletedOnBehalfOf}
            >
                <DropdownQuestion
                    inline
                    hideDivider
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.CompletedOnBehalfOf)}
                    options={useAdminUserOptions()}
                    search
                    selectOnBlur={false}
                />
                <TextQuestion
                    hidden
                    readonly={true}
                    questionConfig={getQuestionConfig(questionIds.CompletedOnBehalfOfName)}
                />
                <TextQuestion
                    hidden
                    readonly={true}
                    questionConfig={getQuestionConfig(questionIds.CompletedOnBehalfOfTitle)}
                />
                <TextQuestion
                    hidden
                    readonly={true}
                    questionConfig={getQuestionConfig(questionIds.CompletedOnBehalfOfQualification)}
                />
                <TextQuestion
                    hidden
                    readonly={true}
                    questionConfig={getQuestionConfig(questionIds.CompletedBy)}
                />
                <TextQuestion
                    hidden
                    readonly={true}
                    questionConfig={getQuestionConfig(questionIds.CompletedByName)}
                />
                <TextQuestion
                    hidden
                    readonly={true}
                    questionConfig={getQuestionConfig(questionIds.CompletedByTitle)}
                />
                <TextQuestion
                    hidden
                    readonly={true}
                    questionConfig={getQuestionConfig(questionIds.CompletedByQualification)}
                />
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Introduction and Data Privacy"
                key={ConsultationSectionIds.IntroductionAndDataPrivacy}
                id={ConsultationSectionIds.IntroductionAndDataPrivacy}
            >
                <p>Hello, it's ……………… here. We have a treatment appointment booked for …………………… today. Are able to do our session today?</p>
                <CheckboxQuestion
                    readonly={props.readonly}
                    hideDivider
                    questionConfig={({
                        id: questionIds.IntroductionAndDataPrivacyConfirmDOB,
                        // eslint-disable-next-line max-len
                        questionText: `Please confirm your DOB? (${props.ohCase && props.ohCase.dateOfBirth && props.ohCase.dateOfBirth.format("L")})`
                    })}
                    value={ConfirmedAnswer}
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    hideDivider
                    questionConfig={({
                        id: questionIds.IntroductionAndDataPrivacyConfirmAddress,
                        // eslint-disable-next-line max-len
                        questionText: `Please could you also confirm the first line of your address and postcode? ${employeeAddress ? `(${employeeAddress})` : ""}`
                    })}
                    value={ConfirmedAnswer}
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    hideDivider={getAnswerToQuestionWithId(questions, questionIds.IntroductionAndDataPrivacyConfirmOtherIdentifier) === ConfirmedAnswer}
                    questionConfig={getQuestionConfig(questionIds.IntroductionAndDataPrivacyConfirmOtherIdentifier)}
                    value={ConfirmedAnswer}
                />
                {getAnswerToQuestionWithId(questions, questionIds.IntroductionAndDataPrivacyConfirmOtherIdentifier) === ConfirmedAnswer &&
                    <TextQuestion
                        inline
                        readonly={props.readonly}
                        questionConfig={introductionAndDataPrivacyConfirmOtherIdentifierDetailsQuestionConfig}
                        multiline
                        textSnippetSelector={
                            <TextSnippetSelector
                                padded
                                onSelect={onIntroductionAndDataPrivacyConfirmOtherIdentifierDetailsSelect}
                            />
                        }
                    />
                }
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.IntroductionAndDataPrivacyConsultationMethod)}
                    options={consultationMethodOptions}
                />
                {
                    consultationMethodAnswer === ConsultationMethodOption.Telephone &&
                    // We only display this section for legacy forms which have the IntroductionAndDataPrivacyRecordingConsent question
                    getAnswerToQuestionWithId(questions, questionIds.IntroductionAndDataPrivacyRecordingConsent) && (
                        <>
                            <Message color="red">
                                *If the individual does not want their call to be recorded,
                                explain that the call recording cannot be stopped but we
                                can arrange for it to be deleted within 48 hours.
                                If the employee is happy with this approach then
                                please email <a href="mailto:itsupport@tphealth.co.uk">itsupport@tphealth.co.uk</a>
                                &nbsp;with details of the phone number and time of call with a request for it to be deleted
                            </Message>
                            <CheckboxQuestion
                                readonly={props.readonly}
                                hideDivider
                                questionConfig={getQuestionConfig(questionIds.IntroductionAndDataPrivacyRecordingConsent)}
                                value={ConsentedAnswer}
                            />
                        </>
                    )}
                {/* If it's a legacy form (i.e. readonly or the above question is answered) then this question used to be conditional
                    so we only render if this question is present */}
                {((props.readonly === false && getAnswerToQuestionWithId(questions, questionIds.IntroductionAndDataPrivacyRecordingConsent) === undefined)
                    || getAnswerToQuestionWithId(questions, questionIds.IntroductionAndDataPrivacyProceedConsent)) && (
                    <CheckboxQuestion
                        readonly={props.readonly}
                        hideDivider
                        questionConfig={getQuestionConfig(questionIds.IntroductionAndDataPrivacyProceedConsent)}
                        value={ConsentedAnswer}
                    />
                )}
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Questionnaires and Risk"
                key={ConsultationSectionIds.QuestionnairesAndRisk}
                id={ConsultationSectionIds.QuestionnairesAndRisk}
            >
                <IAPTQuestionnaire
                    readonly={props.readonly}
                    sectionId={ConsultationSectionIds.QuestionnairesAndRisk}
                    ohCase={props.ohCase}
                    questions={questions}
                    updateAnswer={updateAnswer}
                />
                <Divider hidden />
                <NumberQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    min={0}
                    max={27}
                    questionConfig={getQuestionConfig(questionIds.PsychologicalWellbeingPHQ9Score)}
                    questionClassName={"question-red"}
                    onAnswerChange={onPsychologicalWellbeingPHQ9ScoreAnswerChange}
                />
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly
                    questionConfig={getQuestionConfig(questionIds.PsychologicalWellbeingPHQ9Outcome)}
                    options={psychologicalAssessmentOutcomeQuestionOptions}
                />
                <NumberQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    min={0}
                    max={21}
                    questionConfig={getQuestionConfig(questionIds.PsychologicalWellbeingGAD7Score)}
                    questionClassName={"question-red"}
                    onAnswerChange={onPsychologicalWellbeingGAD7ScoreAnswerChange}
                />
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly
                    questionConfig={getQuestionConfig(questionIds.PsychologicalWellbeingGAD7Outcome)}
                    options={psychologicalAssessmentOutcomeQuestionOptions}
                />
                <NumberQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    min={0}
                    max={40}
                    questionConfig={getQuestionConfig(questionIds.PsychologicalWellbeingWSASScore)}
                    questionClassName={"question-red"}
                    onAnswerChange={onPsychologicalWellbeingWSASScoreAnswerChange}
                />
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly
                    questionConfig={getQuestionConfig(questionIds.PsychologicalWellbeingWSASOutcome)}
                    options={psychologicalAssessmentOutcomeQuestionOptions}
                />
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.RiskAssessmentCompleted)}
                    options={riskAssessmentCompletedOptions}
                />
                {riskAssessmentCompletedAnswer === RiskAssessmentCompleted.Yes && <>
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={({
                            ...getQuestionConfig(questionIds.RiskAssessmentCompletedToSelf) as CustomQuestionConfig,
                            questionElement: (<ul className={"question-ul"}>
                                <li>Current</li>
                                <li>Historical</li>
                            </ul>)
                        })}
                        useExtendedLabel={true}
                        multiline
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={({
                            ...getQuestionConfig(questionIds.RiskAssessmentCompletedToOthers) as CustomQuestionConfig,
                            questionElement: (<ul className={"question-ul"}>
                                <li>Current</li>
                                <li>Historical</li>
                            </ul>)
                        })}
                        useExtendedLabel={true}
                        multiline
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={({
                            ...getQuestionConfig(questionIds.RiskAssessmentCompletedSafeguardingIssues) as CustomQuestionConfig,
                            questionElement: (<ul className={"question-ul"}>
                                <li>Risk from others</li>
                                <li>Risk of self-neglect</li>
                                <li>Risk of neglecting others</li>
                            </ul>)
                        })}
                        useExtendedLabel={true}
                        multiline
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.RiskAssessmentCompletedFactorsAddingToRisk)}
                        multiline
                    />
                </>}
                <TextQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={({
                        ...getQuestionConfig(questionIds.RiskManagementPlan) as CustomQuestionConfig,
                        questionElement: (<ul className={"question-ul"}>
                            <li>Discussed</li>
                            <li>Agreed with employee</li>
                        </ul>)
                    })}
                    useExtendedLabel={true}
                    multiline
                />
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Clinical Notes"
                key={ConsultationSectionIds.ClinicalNotes}
                id={ConsultationSectionIds.ClinicalNotes}
            >
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.ClinicalNotesRiskLetterToGeneralPractitioner)}
                    options={riskLetterToGPOptions}
                />
                {riskLetterToGPAnswer === RiskLetterToGP.Yes && <Message color="blue">Letter to be written to TP on GP Risk Letter Template</Message>}
                <TextQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.ClinicalNotesComments)}
                    multiline
                />
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Standard Clinical Details and Outcome"
                key={ConsultationSectionIds.StandardClinicalDetailsAndOutcome}
                id={ConsultationSectionIds.StandardClinicalDetailsAndOutcome}
            >
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.ClinicalDetailsCurrentWorkStatus)}
                    options={currentWorkStatusOptions}
                />
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.ClinicalDetailsProvisionalDiagnosis)}
                    options={provisionalDiagnosisOptions}
                    questionClassName={"question-red"}
                />
                <MultiCheckboxQuestion
                    hideDivider
                    inline
                    questionConfig={getQuestionConfig(questionIds.ClinicalDetailsCoMorbidConditions)}
                    readonly={props.readonly}
                    options={coMorbidConditionsOptions}
                    questionClassName={"question-red"}
                >
                    {(Object.keys(coMorbidConditionsOptions) as (keyof typeof coMorbidConditionsOptions)[]).map(k =>
                        <MultiCheckbox key={k} value={k} label={coMorbidConditionsOptions[k]} />)}
                </MultiCheckboxQuestion>
                {showClinicalDetailsCoMorbidConditionsOtherQuestion &&
                    <TextQuestion
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.ClinicalDetailsCoMorbidConditionsOther)}
                        multiline
                        mandatory
                        questionClassName={"question-red"}
                    />
                }
                <MultiCheckboxQuestion
                    hideDivider
                    inline
                    questionConfig={getQuestionConfig(questionIds.ClinicalDetailsTreatmentCompletedInTodaysSession)}
                    readonly={props.readonly}
                    options={treatmentCompletedInTodaysSessionOptions}
                >
                    {(Object.keys(treatmentCompletedInTodaysSessionOptions) as (keyof typeof treatmentCompletedInTodaysSessionOptions)[]).map(k =>
                        <MultiCheckbox key={k} value={k} label={treatmentCompletedInTodaysSessionOptions[k]} />)}
                </MultiCheckboxQuestion>
                {showClinicalDetailsTreatmentCompletedInTodaysSessionOtherQuestion &&
                    <TextQuestion
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.ClinicalDetailsTreatmentCompletedInTodaysSessionOther)}
                        multiline
                        mandatory
                    />
                }
                <MultiCheckboxQuestion
                    hideDivider
                    inline
                    questionConfig={getQuestionConfig(questionIds.ClinicalDetailsSessionsSignpostingDischarge)}
                    readonly={props.readonly}
                    options={sessionsSignpostingDischargeOptions}
                >
                    {(Object.keys(sessionsSignpostingDischargeOptions) as (keyof typeof sessionsSignpostingDischargeOptions)[]).map(k =>
                        <MultiCheckbox key={k} value={k} label={sessionsSignpostingDischargeOptions[k]} />)}
                </MultiCheckboxQuestion>
                <MultiCheckboxQuestion
                    hideDivider
                    inline
                    questionConfig={getQuestionConfig(questionIds.ClinicalDetailsSignpostToOtherServices)}
                    readonly={props.readonly}
                    options={signpostToOtherServicesOptions}
                >
                    {(Object.keys(signpostToOtherServicesOptions) as (keyof typeof signpostToOtherServicesOptions)[]).map(k =>
                        <MultiCheckbox key={k} value={k} label={signpostToOtherServicesOptions[k]} />)}
                </MultiCheckboxQuestion>
                {showClinicalDetailsSignpostToOtherServicesOtherQuestion &&
                    <TextQuestion
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.ClinicalDetailsSignpostToOtherServicesOther)}
                        multiline
                        mandatory
                    />
                }
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.ClinicalDetailsOutcomeFinalSessionOnly)}
                    options={outcomeFinalSessionOnlyOptions}
                    questionClassName={"question-red"}
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    hideDivider
                    questionConfig={getQuestionConfig(questionIds.ClinicalDetailsOnwardReferral)}
                    value={ConfirmedAnswer}
                    questionClassName={"question-red"}
                />
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Consent and Report"
                key={ConsultationSectionIds.ConsentAndReport}
                id={ConsultationSectionIds.ConsentAndReport}
                hideNextButton={consentAndReportConsentAnswer === ConsentAndReport.Yes}
            >
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(questionIds.ConsentAndReportConsent)}
                    options={consentAndReportOptions}
                    mandatory
                />
                <Divider hidden />
                {consentAndReportConsentAnswer === ConsentAndReport.Yes &&
                    <>
                        <p>Please clearly outline the content of your report to the employee and then ask them confirm their preference for consent</p>
                        <p><strong>Note:</strong> Once your report has been written it will be managed based on the choice that you make below</p>
                        <RadioButtonQuestion
                            hideDivider={!!scopeOfReportAnswer}
                            inline
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.ConsentAndReportScopeOfReport)}
                            mandatory
                        >
                            <RadioButton
                                label="I would like to see a copy of my report before it is released to HR and my Manager"
                                value={ConsentToReleaseTheReportAnswers.CopyRequired}
                            />
                            <RadioButton
                                label="I am happy for the report to be sent to me, HR and my Manager at the same time"
                                value={ConsentToReleaseTheReportAnswers.CopyRequiredSameTime}
                            />
                            <RadioButton
                            // eslint-disable-next-line max-len
                                label="Neither of the above (choose this option if any of the email addresses below are wrong or if the employee / manager email address is blank)"
                                value={ConsentToReleaseTheReportAnswers.AlternativeScope}
                            />
                        </RadioButtonQuestion>
                        {(() => {
                            switch (scopeOfReportAnswer) {
                                case ConsentToReleaseTheReportAnswers.CopyRequired:
                                // eslint-disable-next-line max-len
                                    return <Segment>If the employee chooses to see the report first you must notify them that the report will be sent to the Optima Health portal and they will receive an email notification telling them that the report is ready. Advise them that they <strong>must</strong> respond to this within 5 Days or the report will be automatically sent to their Manager and HR. <strong>Check the email address of the manager and employee are correct below. If not, or the employee / manager email address is blank you must select the third option (Neither of the above)</strong></Segment>;
                                case ConsentToReleaseTheReportAnswers.CopyRequiredSameTime:
                                // eslint-disable-next-line max-len
                                    return <Segment>Advise the employee that they will receive a copy of the report via the Optima Health portal and that they will receive an email notification telling them the report is ready. <strong>Check the email address of the manager and employee are correct below. If not, or the employee / manager email address is blank you must select the third option (Neither of the above)</strong></Segment>;
                                case ConsentToReleaseTheReportAnswers.AlternativeScope:
                                    return (<TextQuestion
                                        inline
                                        mandatory
                                        readonly={false}
                                        questionConfig={getQuestionConfig(questionIds.ConsentAndReportAlternativeConsentChoice)}
                                        multiline
                                    />);
                                default:
                                    return null;
                            }
                        })()}
                        <TextQuestion
                            inline
                            readonly
                            questionConfig={getQuestionConfig(questionIds.EmployeeEmailAddress)}
                            answer={props.employeeRecord?.personalEmailAddress}
                        />
                        <TextQuestion
                            inline
                            readonly
                            questionConfig={getQuestionConfig(questionIds.ManagerEmailAddress)}
                            answer={consultationDetails?.lineManagersEmails?.join(" & ")}
                        />
                        <TextQuestion
                            inline
                            readonly
                            questionConfig={getQuestionConfig(questionIds.HREmailAddress)}
                            answer={consultationDetails?.hrEmails?.join(" & ")}
                        />
                        <Divider hidden />
                    </>
                }
            </AccordionQuestionnaireSection>
            {
                !props.readonly &&
                consentAndReportConsentAnswer === ConsentAndReport.No &&
                nextTaskTypes !== null &&
                    <AccordionQuestionnaireSection
                        title="Next Tasks"
                        key={ConsultationSectionIds.NextTasks}
                        id={ConsultationSectionIds.NextTasks}
                        neverHide={true}
                        hideNextButton={true}
                        additionalIsValid={nextTasks?.every(t => t.isValid)}
                    >
                        <Form>
                            <label><strong>Next Tasks</strong></label>
                            <AddTasksInputs
                                taskTypes={nextTaskTypes}
                                onChange={setNextTasks}
                                ohCaseId={props.ohCase?.id}
                                showErrors={submitted}
                                assignmentOptionRequired={true}
                            />
                            <Divider hidden />
                        </Form>
                    </AccordionQuestionnaireSection>
            }
        </AccordionQuestionnaireContainer>
    );
};
